import * as yup from 'yup'
import type { FieldValues, Resolver } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

export type TranslateFn = (key: string, options?: Record<string, unknown>) => string

export type OrgSettingsFormValues = {
  name: string
  street: string
  city: string
  country: string
  postalCode: string
}

export type CreateOrganizationFormValues = OrgSettingsFormValues & {
  maxEmployees: string
  maxSprints: string
}

export const createOrgSettingsSchema = (t: TranslateFn) =>
  yup
    .object({
      name: yup.string().trim().required(t('org.settings.nameRequired')),
      street: yup.string().trim().required(t('org.settings.streetRequired')),
      city: yup.string().trim().required(t('org.settings.cityRequired')),
      country: yup.string().trim().required(t('org.settings.countryRequired')),
      postalCode: yup.string().trim().required(t('org.settings.postalCodeRequired'))
    })
    .required() as yup.ObjectSchema<Record<string, unknown>>

export const createOrganizationWizardSchema = (t: TranslateFn) =>
  createOrgSettingsSchema(t)
    .shape({
      maxEmployees: yup
        .string()
        .trim()
        .required(t('org.create.maxEmployeesRequired'))
        .test('min-employees', t('org.create.maxEmployeesMin'), (value) => {
          const parsed = Number.parseInt(value ?? '', 10)
          return Number.isFinite(parsed) && parsed >= 1
        }),
      maxSprints: yup
        .string()
        .trim()
        .test('optional-sprints', t('org.create.maxSprintsMin'), (value) => {
          if (!value?.trim()) return true
          const parsed = Number.parseInt(value, 10)
          return Number.isFinite(parsed) && parsed >= 1
        })
    })
    .required() as yup.ObjectSchema<Record<string, unknown>>

export const buildOrgFormResolver = <TFieldValues extends FieldValues>(schema: yup.AnyObjectSchema) =>
  yupResolver(schema as never) as unknown as Resolver<TFieldValues>
